import type { ReactNode } from "react";
import { cn, type IconName } from "@strategy4kids/ui";
import { personaById } from "@strategy4kids/persona";
import { usePersonaStore } from "../../state/persona-store.js";
import { usePlayerLookStore } from "../../state/player-look-store.js";
import { SeatPlate } from "./SeatPlate.js";

export interface PlayerBarProps {
  readonly testid: string;
  readonly name: string;
  readonly portrait?: string | null;
  readonly fallbackIcon?: IconName;
  /** It is this side's move: the plate is ringed. */
  readonly active?: boolean;
  readonly you?: boolean;
  /** What the character just said, shown beside the plate. */
  readonly bubble?: string | null;
  /** Captured material, pip count, clock... shown at the right end of the bar. */
  readonly children?: ReactNode;
  readonly className?: string;
}

/**
 * A slim horizontal bar for one side of a two-player board game (chess, checkers, backgammon):
 * the opponent sits above the board, you below. Built on {@link SeatPlate}.
 */
export function PlayerBar({
  testid,
  name,
  portrait,
  fallbackIcon,
  active,
  you,
  bubble,
  children,
  className,
}: PlayerBarProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center gap-x-3 gap-y-2 [@media(max-height:820px)]:[&_section>div]:!py-1 [@media(max-height:820px)]:[&_section>div>span:first-child]:!size-10 max-sm:[&_section>div]:!py-1 max-sm:[&_section>div>span:first-child]:!size-10",
        className,
      )}
      data-testid={testid}
      data-active={active || undefined}
    >
      <SeatPlate
        testid={`${testid}-seat`}
        name={name}
        portrait={portrait}
        fallbackIcon={fallbackIcon}
        active={active}
        you={you}
      />
      {bubble ? (
        <p
          className="glass glass-light max-w-[22rem] rounded-2xl px-3 py-1.5 text-sm font-semibold text-fg shadow-md lg:text-base"
          data-testid={`${testid}-bubble`}
          aria-live="polite"
        >
          {bubble}
        </p>
      ) : null}
      {children ? (
        <div className="ml-auto flex flex-wrap items-center gap-2 text-sm font-bold text-fg lg:text-base">
          {children}
        </div>
      ) : null}
    </div>
  );
}

const GLYPH: Record<string, string> = {
  p: "♟",
  n: "♞",
  b: "♝",
  r: "♜",
  q: "♛",
  k: "♚",
  m: "●",
  x: "◆",
};

/** Pieces a side has taken, as small glyphs (chess pieces, or discs for checkers). */
export function CapturedPieces({
  pieces,
  label,
  testid,
}: {
  readonly pieces: readonly string[];
  readonly label: string;
  readonly testid: string;
}) {
  if (pieces.length === 0) return null;
  return (
    <span
      className="flex items-center gap-1"
      data-testid={testid}
      aria-label={`${label}: ${pieces.length}`}
    >
      <span
        className="tracking-tight text-lg text-fg lg:text-2xl"
        aria-hidden="true"
      >
        {pieces.map((c) => GLYPH[c.toLowerCase()] ?? "●").join("")}
      </span>
    </span>
  );
}

/** Who sits on each side of a duel: the player (onboarding name + buddy) and the chosen persona. */
export function useDuelIdentity(youLabel: string, opponentLabel: string) {
  const look = usePlayerLookStore();
  const personaId = usePersonaStore((s) => s.personaId);
  const persona = personaById(personaId);
  return {
    you: {
      name: look.name || youLabel,
      portrait: null as string | null,
      fallbackIcon: look.buddy as IconName,
    },
    opponent: {
      name: persona?.name ?? opponentLabel,
      portrait: persona?.portrait ?? null,
      fallbackIcon: "Bot" as IconName,
    },
  };
}
